/**
 * Comprehensive Form Validation Utility
 *
 * Validates all form fields with specific rules for dates, emails, phones, etc.
 * Catches common formatting errors like DD/MM/YYYY instead of MM/DD/YYYY
 */

// ============================================
// DATE VALIDATION (PRIORITY)
// ============================================

/**
 * Validate date format is MM/DD/YYYY (US standard)
 * This catches European DD/MM/YYYY format mistakes like "19/10/2033"
 *
 * @param {string} dateString - Date string to validate
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateDateFormat(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    return { isValid: false, error: 'Date is required' };
  }

  const trimmed = dateString.trim();

  // Check for ISO format (YYYY-MM-DD) - this is valid
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    return validateDateComponents(month, day, year);
  }

  // Check for MM/DD/YYYY format
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Please enter date as MM/DD/YYYY (e.g., 10/19/2033)'
    };
  }

  const [month, day, year] = trimmed.split('/').map(Number);
  return validateDateComponents(month, day, year);
}

/**
 * Validate individual date components (month, day, year)
 */
function validateDateComponents(month, day, year) {
  // Validate month (1-12)
  if (month < 1 || month > 12) {
    // Check if this looks like DD/MM/YYYY format (day > 12)
    if (month > 12 && month <= 31) {
      return {
        isValid: false,
        error: `Invalid format. Use MM/DD/YYYY (you may have entered DD/MM/YYYY)`
      };
    }
    return {
      isValid: false,
      error: 'Month must be between 01 and 12'
    };
  }

  // Validate year (reasonable range)
  if (year < 1900 || year > 2100) {
    return {
      isValid: false,
      error: 'Please enter a valid 4-digit year'
    };
  }

  // Validate day for the specific month
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) {
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return {
      isValid: false,
      error: `Invalid day. ${monthNames[month]} only has ${daysInMonth} days`
    };
  }

  return { isValid: true, error: null };
}

/**
 * Check if a date string represents a valid, parseable date
 */
export function isValidDate(dateString) {
  const formatResult = validateDateFormat(dateString);
  if (!formatResult.isValid) {
    return formatResult;
  }

  // Try to parse and verify
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) {
    return { isValid: false, error: 'Invalid date' };
  }

  return { isValid: true, error: null };
}

/**
 * Parse date string to Date object
 */
export function parseDate(dateString) {
  if (!dateString) return null;

  const trimmed = dateString.trim();

  // ISO format YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T00:00:00');
  }

  // MM/DD/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [month, day, year] = trimmed.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  return null;
}

/**
 * Check if date is in the future
 * For ID expiration dates - must be after today
 */
export function isFutureDate(dateString) {
  const formatResult = validateDateFormat(dateString);
  if (!formatResult.isValid) {
    return formatResult;
  }

  const date = parseDate(dateString);
  if (!date) {
    return { isValid: false, error: 'Invalid date' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (date <= today) {
    return {
      isValid: false,
      error: 'This date must be in the future'
    };
  }

  return { isValid: true, error: null };
}

/**
 * Check if date is in the past
 * For birth dates and ID issue dates - must be before today
 */
export function isPastDate(dateString) {
  const formatResult = validateDateFormat(dateString);
  if (!formatResult.isValid) {
    return formatResult;
  }

  const date = parseDate(dateString);
  if (!date) {
    return { isValid: false, error: 'Invalid date' };
  }

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  if (date >= today) {
    return {
      isValid: false,
      error: 'This date must be in the past'
    };
  }

  return { isValid: true, error: null };
}

/**
 * Validate date of birth - must be past, reasonable age
 */
export function validateDateOfBirth(dateString) {
  if (!dateString || dateString.trim() === '') {
    return { isValid: false, error: 'Date of birth is required' };
  }

  // First check format
  const formatResult = validateDateFormat(dateString);
  if (!formatResult.isValid) {
    return formatResult;
  }

  // Check it's in the past
  const pastResult = isPastDate(dateString);
  if (!pastResult.isValid) {
    return { isValid: false, error: 'Birth date cannot be in the future' };
  }

  // Check reasonable age (0-120 years)
  const date = parseDate(dateString);
  const today = new Date();
  const age = today.getFullYear() - date.getFullYear();

  if (age > 120) {
    return { isValid: false, error: 'Please enter a valid birth date' };
  }

  return { isValid: true, error: null };
}

/**
 * Validate ID expiration date - must be in future
 */
export function validateExpirationDate(dateString) {
  if (!dateString || dateString.trim() === '') {
    return { isValid: false, error: 'Expiration date is required' };
  }

  // First check format
  const formatResult = validateDateFormat(dateString);
  if (!formatResult.isValid) {
    return formatResult;
  }

  // Check it's in the future
  const futureResult = isFutureDate(dateString);
  if (!futureResult.isValid) {
    return { isValid: false, error: 'ID expiration date must be in the future' };
  }

  return { isValid: true, error: null };
}

// ============================================
// PHONE VALIDATION
// ============================================

/**
 * Validate US phone number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validatePhone(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  // Extract just the digits
  const digits = phoneNumber.replace(/\D/g, '');

  // Must have exactly 10 digits
  if (digits.length < 10) {
    return {
      isValid: false,
      error: `Phone number must be 10 digits (you entered ${digits.length})`
    };
  }

  if (digits.length > 10) {
    return {
      isValid: false,
      error: 'Phone number should be 10 digits'
    };
  }

  // Check area code is valid (not starting with 0 or 1)
  if (digits[0] === '0' || digits[0] === '1') {
    return {
      isValid: false,
      error: 'Invalid area code'
    };
  }

  return { isValid: true, error: null };
}

// ============================================
// ZIP CODE VALIDATION
// ============================================

/**
 * Validate US ZIP code
 * @param {string} zipCode - ZIP code to validate
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateZipCode(zipCode) {
  if (!zipCode || typeof zipCode !== 'string') {
    return { isValid: false, error: 'ZIP code is required' };
  }

  const trimmed = zipCode.trim();

  // 5-digit format
  if (/^\d{5}$/.test(trimmed)) {
    return { isValid: true, error: null };
  }

  // 5+4 format (12345-6789)
  if (/^\d{5}-\d{4}$/.test(trimmed)) {
    return { isValid: true, error: null };
  }

  // Check if partial
  if (/^\d+$/.test(trimmed) && trimmed.length < 5) {
    return {
      isValid: false,
      error: `ZIP code must be 5 digits (you entered ${trimmed.length})`
    };
  }

  return {
    isValid: false,
    error: 'Please enter a valid 5-digit ZIP code'
  };
}

// ============================================
// NAME VALIDATION
// ============================================

/**
 * Validate name field
 * @param {string} name - Name to validate
 * @param {boolean} required - Whether the field is required
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateName(name, required = true) {
  if (!name || typeof name !== 'string') {
    if (required) {
      return { isValid: false, error: 'This field is required' };
    }
    return { isValid: true, error: null };
  }

  const trimmed = name.trim();

  if (required && trimmed.length === 0) {
    return { isValid: false, error: 'This field is required' };
  }

  if (trimmed.length > 0 && trimmed.length < 2) {
    return { isValid: false, error: 'Name must be at least 2 characters' };
  }

  // Allow letters, spaces, hyphens, apostrophes
  // eslint-disable-next-line no-useless-escape
  if (!/^[a-zA-Z\s\-'\.]+$/.test(trimmed) && trimmed.length > 0) {
    return {
      isValid: false,
      error: 'Name can only contain letters, spaces, hyphens, and apostrophes'
    };
  }

  return { isValid: true, error: null };
}

// ============================================
// SSN / ITIN VALIDATION
// ============================================

/**
 * Validate SSN or ITIN format
 *
 * SSN Rules:
 * - 9 digits
 * - Cannot start with 000, 666, or 9XX (those starting with 9 are ITINs)
 *
 * ITIN Rules:
 * - 9 digits
 * - Must start with 9
 * - Second and third digits: 70-88, 90-92, 94-99
 *
 * @param {string} ssn - SSN or ITIN to validate
 * @returns {{ isValid: boolean, error: string|null, isITIN?: boolean }}
 */
export function validateSSN(ssn) {
  if (!ssn || typeof ssn !== 'string') {
    return { isValid: false, error: 'SSN or ITIN is required' };
  }

  // Extract just the digits
  const digits = ssn.replace(/\D/g, '');

  if (digits.length < 9) {
    return {
      isValid: false,
      error: `SSN/ITIN must be 9 digits (you entered ${digits.length})`
    };
  }

  if (digits.length > 9) {
    return { isValid: false, error: 'SSN/ITIN should be 9 digits' };
  }

  // Check for invalid numbers (all same digit)
  if (/^(\d)\1{8}$/.test(digits)) {
    return { isValid: false, error: 'Please enter a valid SSN or ITIN' };
  }

  // Check if this is an ITIN (starts with 9)
  if (digits.startsWith('9')) {
    // ITINs are valid - they start with 9 and are used by individuals
    // who need a tax ID but aren't eligible for an SSN
    return { isValid: true, error: null, isITIN: true };
  }

  // SSN validation (doesn't start with 9)
  if (digits.startsWith('000') || digits.startsWith('666')) {
    return { isValid: false, error: 'Invalid SSN' };
  }

  return { isValid: true, error: null, isITIN: false };
}

// ============================================
// REQUIRED FIELD VALIDATION
// ============================================

/**
 * Validate a required field is not empty
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateRequired(value, fieldName = 'This field') {
  if (value === null || value === undefined) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (typeof value === 'string' && value.trim() === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  return { isValid: true, error: null };
}

// ============================================
// DRIVER'S LICENSE / ID VALIDATION
// ============================================

/**
 * Validate driver's license / ID number
 * @param {string} idNumber - ID number to validate
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateIDNumber(idNumber) {
  if (!idNumber || typeof idNumber !== 'string') {
    return { isValid: false, error: 'ID number is required' };
  }

  const trimmed = idNumber.trim();

  if (trimmed.length < 4) {
    return { isValid: false, error: 'ID number must be at least 4 characters' };
  }

  if (trimmed.length > 20) {
    return { isValid: false, error: 'ID number is too long' };
  }

  // Allow alphanumeric and some special chars
  // eslint-disable-next-line no-useless-escape
  if (!/^[a-zA-Z0-9\-\*]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'ID number can only contain letters, numbers, and hyphens'
    };
  }

  return { isValid: true, error: null };
}

// ============================================
// PAGE 1 COMPLETE VALIDATION
// ============================================

/**
 * Validate all Page 1 fields
 * Returns an object with all errors
 *
 * @param {object} formData - The form data object
 * @returns {{ isValid: boolean, errors: object }}
 */
export function validatePage1(formData) {
  const errors = {};

  // === TAXPAYER NAME ===
  const firstNameResult = validateName(formData.taxpayerFirstName, true);
  if (!firstNameResult.isValid) {
    errors.taxpayerFirstName = firstNameResult.error;
  }

  const lastNameResult = validateName(formData.taxpayerLastName, true);
  if (!lastNameResult.isValid) {
    errors.taxpayerLastName = lastNameResult.error;
  }

  // Middle name is optional
  if (formData.taxpayerMiddleName) {
    const middleResult = validateName(formData.taxpayerMiddleName, false);
    if (!middleResult.isValid) {
      errors.taxpayerMiddleName = middleResult.error;
    }
  }

  // === DATE OF BIRTH ===
  const dobResult = validateDateOfBirth(formData.dateOfBirth);
  if (!dobResult.isValid) {
    errors.dateOfBirth = dobResult.error;
  }

  // === FILING STATUS ===
  if (!formData.filingStatus || formData.filingStatus.trim() === '') {
    errors.filingStatus = 'Please select a filing status';
  }

  // === INCOME SOURCE ===
  if (!formData.incomeSource || formData.incomeSource.trim() === '') {
    errors.incomeSource = 'Please select a source of income';
  }

  // === ADDRESS ===
  if (!formData.streetAddress || formData.streetAddress.trim() === '') {
    errors.streetAddress = 'Street address is required';
  }

  if (!formData.city || formData.city.trim() === '') {
    errors.city = 'City is required';
  }

  if (!formData.state || formData.state.trim() === '') {
    errors.state = 'State is required';
  }

  const zipResult = validateZipCode(formData.postalCode);
  if (!zipResult.isValid) {
    errors.postalCode = zipResult.error;
  }

  // === PHONE NUMBER ===
  const phoneResult = validatePhone(formData.phoneNumber);
  if (!phoneResult.isValid) {
    errors.phoneNumber = phoneResult.error;
  }

  // === EMAIL ===
  // Using existing email validator
  const { validateEmail } = require('./emailValidator');
  const emailResult = validateEmail(formData.email);
  if (!emailResult.isValid) {
    errors.email = emailResult.error;
  }

  // === SSN ===
  const ssnResult = validateSSN(formData.ssn);
  if (!ssnResult.isValid) {
    errors.ssn = ssnResult.error;
  }

  // === DRIVER'S LICENSE ===
  const idResult = validateIDNumber(formData.driversLicense);
  if (!idResult.isValid) {
    errors.driversLicense = idResult.error;
  }

  // === ID EXPIRATION DATE ===
  const expResult = validateExpirationDate(formData.idExpirationDate);
  if (!expResult.isValid) {
    errors.idExpirationDate = expResult.error;
  }

  // === HEALTH INSURANCE ===
  if (!formData.hasMarketplaceInsurance || formData.hasMarketplaceInsurance.trim() === '') {
    errors.hasMarketplaceInsurance = 'Please select an option';
  }

  // Conditional: if No, must answer wantsHealthInsurance
  if (formData.hasMarketplaceInsurance === 'No' &&
      (!formData.wantsHealthInsurance || formData.wantsHealthInsurance.trim() === '')) {
    errors.wantsHealthInsurance = 'Please select an option';
  }

  // === LIFE INSURANCE ===
  if (!formData.hasLifeInsurance || formData.hasLifeInsurance.trim() === '') {
    errors.hasLifeInsurance = 'Please select an option';
  }

  // Conditional: if No, must answer wantsLifeInsurance
  if (formData.hasLifeInsurance === 'No' &&
      (!formData.wantsLifeInsurance || formData.wantsLifeInsurance.trim() === '')) {
    errors.wantsLifeInsurance = 'Please select an option';
  }

  // === HOME OWNERSHIP ===
  if (!formData.homeOwnership || formData.homeOwnership.trim() === '') {
    errors.homeOwnership = 'Please select an option';
  }

  // Conditional: if Rent, must answer wantsToBuyHome
  if (formData.homeOwnership === 'Rent' &&
      (!formData.wantsToBuyHome || formData.wantsToBuyHome.trim() === '')) {
    errors.wantsToBuyHome = 'Please select an option';
  }

  // === SPOUSE FIELDS (if Married Filing Jointly) ===
  if (formData.filingStatus === 'Married Filing Jointly') {
    // Spouse First Name
    const spouseFirstResult = validateName(formData.spouseFirstName, true);
    if (!spouseFirstResult.isValid) {
      errors.spouseFirstName = spouseFirstResult.error || 'Spouse first name is required';
    }

    // Spouse Last Name
    const spouseLastResult = validateName(formData.spouseLastName, true);
    if (!spouseLastResult.isValid) {
      errors.spouseLastName = spouseLastResult.error || 'Spouse last name is required';
    }

    // Spouse DOB
    const spouseDobResult = validateDateOfBirth(formData.spouseDateOfBirth);
    if (!spouseDobResult.isValid) {
      errors.spouseDateOfBirth = spouseDobResult.error || 'Spouse date of birth is required';
    }

    // Spouse SSN
    const spouseSsnResult = validateSSN(formData.spouseSsn);
    if (!spouseSsnResult.isValid) {
      errors.spouseSsn = spouseSsnResult.error || 'Spouse SSN is required';
    }

    // Spouse Driver's License
    const spouseIdResult = validateIDNumber(formData.spouseDriversLicense);
    if (!spouseIdResult.isValid) {
      errors.spouseDriversLicense = spouseIdResult.error || 'Spouse ID number is required';
    }

    // Spouse ID Expiration
    const spouseExpResult = validateExpirationDate(formData.spouseIdExpirationDate);
    if (!spouseExpResult.isValid) {
      errors.spouseIdExpirationDate = spouseExpResult.error || 'Spouse ID expiration is required';
    }
  }

  // === DEPENDENTS ===
  if (formData.claimingDependents === 'Yes') {
    if (!formData.numberOfDependents || formData.numberOfDependents === '') {
      errors.numberOfDependents = 'Please select number of dependents';
    }

    // Validate each dependent
    const numDependents = parseInt(formData.numberOfDependents) || 0;
    const dependents = formData.dependents || [];

    for (let i = 0; i < numDependents; i++) {
      const dep = dependents[i] || {};

      // First Name
      if (!dep.firstName || dep.firstName.trim() === '') {
        errors[`dependent${i}FirstName`] = `Dependent ${i + 1} first name is required`;
      }

      // Last Name
      if (!dep.lastName || dep.lastName.trim() === '') {
        errors[`dependent${i}LastName`] = `Dependent ${i + 1} last name is required`;
      }

      // Relationship
      if (!dep.relationship || dep.relationship.trim() === '') {
        errors[`dependent${i}Relationship`] = `Dependent ${i + 1} relationship is required`;
      }

      // SSN
      if (dep.ssn) {
        const depSsnResult = validateSSN(dep.ssn);
        if (!depSsnResult.isValid) {
          errors[`dependent${i}Ssn`] = depSsnResult.error;
        }
      } else {
        errors[`dependent${i}Ssn`] = `Dependent ${i + 1} SSN is required`;
      }

      // DOB
      if (dep.dateOfBirth) {
        const depDobResult = validateDateOfBirth(dep.dateOfBirth);
        if (!depDobResult.isValid) {
          errors[`dependent${i}DateOfBirth`] = depDobResult.error;
        }
      } else {
        errors[`dependent${i}DateOfBirth`] = `Dependent ${i + 1} date of birth is required`;
      }

      // Activity
      if (!dep.activity || dep.activity.trim() === '') {
        errors[`dependent${i}Activity`] = `Dependent ${i + 1} activity is required`;
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}

/**
 * Validate a single field by name
 *
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value of the field
 * @param {object} formData - Full form data (for conditional validation)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export function validateField(fieldName, value, formData = {}) {
  switch (fieldName) {
    // Names
    case 'taxpayerFirstName':
    case 'taxpayerLastName':
    case 'spouseFirstName':
    case 'spouseLastName':
      return validateName(value, true);

    case 'taxpayerMiddleName':
    case 'spouseMiddleName':
      return validateName(value, false);

    // Dates
    case 'dateOfBirth':
    case 'spouseDateOfBirth':
      return validateDateOfBirth(value);

    case 'idExpirationDate':
    case 'spouseIdExpirationDate':
      return validateExpirationDate(value);

    // Contact
    case 'phoneNumber':
      return validatePhone(value);

    case 'email':
      const { validateEmail } = require('./emailValidator');
      return validateEmail(value);

    // IDs
    case 'ssn':
    case 'spouseSsn':
      return validateSSN(value);

    case 'driversLicense':
    case 'spouseDriversLicense':
      return validateIDNumber(value);

    // Address
    case 'postalCode':
      return validateZipCode(value);

    case 'streetAddress':
    case 'city':
    case 'state':
      return validateRequired(value);

    // Selects
    case 'filingStatus':
    case 'incomeSource':
    case 'hasMarketplaceInsurance':
    case 'wantsHealthInsurance':
    case 'hasLifeInsurance':
    case 'wantsLifeInsurance':
    case 'homeOwnership':
    case 'wantsToBuyHome':
    case 'claimingDependents':
    case 'numberOfDependents':
      return validateRequired(value, 'Please select an option');

    default:
      return { isValid: true, error: null };
  }
}

const formValidation = {
  validateDateFormat,
  isValidDate,
  parseDate,
  isFutureDate,
  isPastDate,
  validateDateOfBirth,
  validateExpirationDate,
  validatePhone,
  validateZipCode,
  validateName,
  validateSSN,
  validateRequired,
  validateIDNumber,
  validatePage1,
  validateField
};

export default formValidation;
